"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createRelayClient, type RelayClient } from "../lib/client";
import type { Catalog } from "../lib/types";

export interface UseRelayConfigOptions {
  /** Pre-built client. Takes precedence over apiBaseUrl. */
  client?: RelayClient;
  /** Used to build a client when `client` is not supplied. */
  apiBaseUrl?: string;
  /** Stops the request and clears the current snapshot. Defaults to true. */
  enabled?: boolean;
  /** Optional automatic REST refresh cadence in milliseconds. Disabled when omitted or <= 0. */
  refetchInterval?: number;
  onError?: (error: Error) => void;
}

export interface UseRelayConfigResult {
  /** Latest GET /v1/config response. */
  config?: Catalog;
  loading: boolean;
  error?: Error;
  /** Re-fetches GET /v1/config using the current client. */
  refresh: () => void;
}

/**
 * Public read-only hook for Relay's origin/destination/target catalog.
 *
 * The response is a normal REST snapshot, not an SSE stream. Use
 * `refetchInterval` for periodic refreshes or call `refresh` for an immediate
 * read without replacing the client or remounting the component.
 */
export function useRelayConfig(opts: UseRelayConfigOptions = {}): UseRelayConfigResult {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const client = useMemo(() => {
    if (opts.client) return opts.client;
    if (!opts.apiBaseUrl) {
      throw new Error("useRelayConfig: either `client` or `apiBaseUrl` must be provided");
    }
    return createRelayClient({ baseUrl: opts.apiBaseUrl });
  }, [opts.client, opts.apiBaseUrl]);

  const enabled = opts.enabled ?? true;
  const refetchInterval = opts.refetchInterval;
  const [config, setConfig] = useState<Catalog>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setConfig(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    setConfig(undefined);
    setLoading(true);
    setError(undefined);

    async function refetchConfig() {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const result = await client.getConfig();
        if (cancelled) return;
        setConfig(result);
        setError(undefined);
      } catch (err) {
        if (cancelled) return;
        const nextError = err instanceof Error ? err : new Error(String(err));
        setError(nextError);
        optsRef.current.onError?.(nextError);
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    }

    void refetchConfig();
    const refetchTimer =
      typeof refetchInterval === "number" &&
      Number.isFinite(refetchInterval) &&
      refetchInterval > 0
        ? setInterval(() => {
            void refetchConfig();
          }, refetchInterval)
        : undefined;

    return () => {
      cancelled = true;
      if (refetchTimer) clearInterval(refetchTimer);
    };
  }, [client, enabled, refreshNonce, refetchInterval]);

  const refresh = useCallback(() => setRefreshNonce((value) => value + 1), []);

  return { config, loading, error, refresh };
}
